import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  BieImportDecisionCode,
} from './bieimportdecision.apis';
import {
  IBieOnboardingValidationIssue,
} from './bieonboardingworkbench.models';

export interface IBieImportDecisionDialogProps {
  open: boolean;
  issue: IBieOnboardingValidationIssue | null;
  decisionCode: BieImportDecisionCode;
  reason: string;
  loading?: boolean;
  errorMessage?: string;
  onDecisionCodeChange: (code: BieImportDecisionCode) => void;
  onReasonChange: (reason: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}

const firstValue = (object: any, keys: string[]): unknown => {
  if (!object || typeof object !== 'object') return undefined;
  for (const key of keys) {
    if (object[key] !== undefined && object[key] !== null) return object[key];
  }
  for (const value of Object.values(object)) {
    if (value && typeof value === 'object') {
      const found = firstValue(value, keys);
      if (found !== undefined && found !== null) return found;
    }
  }
  return undefined;
};

const displayValue = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return 'Not available';
  if (typeof value === 'number') return value.toLocaleString(undefined, { maximumFractionDigits: 6 });
  return String(value);
};

export default function BieImportDecisionDialog(
  props: IBieImportDecisionDialogProps,
): React.ReactElement {
  const {
    open,
    issue,
    decisionCode,
    reason,
    loading = false,
    errorMessage,
    onDecisionCodeChange,
    onReasonChange,
    onClose,
    onConfirm,
  } = props;

  const details = issue?.vdd || {};
  const existingValue = firstValue(details, [
    'existingValue', 'existingvalue', 'existingAmount', 'existingamount',
    'canonicalValue', 'canonicalvalue', 'existing',
  ]);
  const incomingValue = firstValue(details, [
    'incomingValue', 'incomingvalue', 'incomingAmount', 'incomingamount',
    'sourceValue', 'sourcevalue', 'incoming',
  ]) ?? issue?.src;
  const published = Boolean(firstValue(details, [
    'published', 'datasetPublished', 'datasetpublished', 'publishedAt', 'publishedat',
  ]));

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Resolve Existing Canonical Fact</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <Typography variant="body2" color="text.secondary">
            Choose how the incoming staged record should relate to the existing canonical fact.
            The validation issue remains recorded in the audit trail.
          </Typography>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Existing Value</Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>{displayValue(existingValue)}</Typography>
            </Box>
            <Box sx={{ flex: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Incoming Value</Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>{displayValue(incomingValue)}</Typography>
            </Box>
          </Stack>

          {published && (
            <Alert severity="info">
              The existing fact belongs to a published dataset. Replace is not permitted; use Keep or Revise.
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant={decisionCode === 'KEEP' ? 'contained' : 'outlined'}
              onClick={() => onDecisionCodeChange('KEEP')}
              disabled={loading}
            >
              Keep Existing
            </Button>
            <Button
              variant={decisionCode === 'REPLACE' ? 'contained' : 'outlined'}
              onClick={() => onDecisionCodeChange('REPLACE')}
              disabled={loading || published}
            >
              Replace Existing
            </Button>
            <Button
              variant={decisionCode === 'REVISE' ? 'contained' : 'outlined'}
              onClick={() => onDecisionCodeChange('REVISE')}
              disabled={loading}
            >
              Create Revision
            </Button>
          </Stack>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label={decisionCode === 'KEEP' ? 'Decision reason or note' : 'Decision reason'}
            value={reason}
            disabled={loading}
            required={decisionCode !== 'KEEP'}
            onChange={(event) => onReasonChange(event.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={loading || (decisionCode !== 'KEEP' && !reason.trim())}
        >
          Apply and Revalidate
        </Button>
      </DialogActions>
    </Dialog>
  );
}
